"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";

import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { API_BASE_URL, authApi } from "@/lib/client-api";
import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { profileSetupHrefFor, sanitizeNextPath } from "@/lib/navigation";
import { useMounted } from "@/lib/use-mounted";

type AuthMode = "email" | "phone" | "register" | "forgot" | "reset";

const verificationRequests = new Map<string, Promise<{ verified: true }>>();

function confirmEmailOnce(token: string) {
  const pending = verificationRequests.get(token);
  if (pending) return pending;
  const request = authApi.confirmEmail(token);
  verificationRequests.set(token, request);
  return request;
}

export default function AuthPage() {
  return (
    <Suspense fallback={<div className="protected-panel">Loading sign in...</div>}>
      <AuthInner />
    </Suspense>
  );
}

function AuthInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = sanitizeNextPath(searchParams.get("next"));
  const context = searchParams.get("context");
  const resetToken = searchParams.get("resetToken");
  const verifyEmailToken = searchParams.get("verifyEmailToken");
  const mounted = useMounted();
  const sessionUser = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const setSession = useAuthStore((state) => state.setSession);
  const startDemoSession = useAuthStore((state) => state.startDemoSession);

  const [mode, setMode] = useState<AuthMode>(resetToken ? "reset" : "email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const finish = useCallback((result?: { isNewUser?: boolean; user?: { isProfileComplete?: boolean | null } }) => {
    if (result?.isNewUser || result?.user?.isProfileComplete === false) {
      router.replace(profileSetupHrefFor(next));
      return;
    }
    router.replace(next);
  }, [next, router]);

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setError(null);
    setMessage(null);
  };

  useEffect(() => {
    if (
      !mounted ||
      !sessionUser ||
      !isUsableWebSession({ user: sessionUser, accessToken, demoMode })
    ) {
      return;
    }
    finish({ user: sessionUser });
  }, [accessToken, demoMode, finish, mounted, sessionUser]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (err: any) {
      const raw = err?.response?.data?.message ?? err?.message;
      const status = err?.response?.status;
      const messageText = Array.isArray(raw) ? raw[0] : raw ? String(raw) : "";
      if (!err?.response && (err?.code === "ERR_NETWORK" || messageText.toLowerCase().includes("network"))) {
        setError(`Could not reach the Skylight API at ${API_BASE_URL}. Check the web-client API URL and Caddy/API routing.`);
      } else {
        setError(status ? `[${status}] ${messageText || "Could not complete sign in."}` : messageText || "Could not complete sign in.");
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!verifyEmailToken) return;
    let active = true;
    setBusy(true);
    setError(null);
    confirmEmailOnce(verifyEmailToken)
      .then(() => {
        if (active) setMessage("Your email address is verified. You can sign in now.");
      })
      .catch((err: any) => {
        if (active) {
          const raw = err?.response?.data?.message ?? err?.message;
          setError(Array.isArray(raw) ? raw[0] : String(raw || "Verification link is invalid or expired."));
        }
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [verifyEmailToken]);

  const loginEmail = () =>
    run(async () => {
      const result = await authApi.emailLogin(email, password);
      setSession(result);
      finish(result);
    });

  const registerEmail = () =>
    run(async () => {
      const cleanDisplayName = displayName.trim();
      const cleanUsername = username.trim();
      const cleanEmail = email.trim().toLowerCase();
      if (!cleanDisplayName) throw new Error("Enter your display name.");
      if (!cleanUsername) throw new Error("Choose a username.");
      if (!cleanEmail) throw new Error("Enter your email address.");
      if (password.length < 8) throw new Error("Password must be at least 8 characters.");
      const result = await authApi.emailRegister({
        email: cleanEmail,
        password,
        displayName: cleanDisplayName,
        username: cleanUsername,
      });
      if (!result?.accessToken || !result?.user) {
        throw new Error("Account was created but the API did not return a login session.");
      }
      setSession(result);
      finish(result);
    });

  const sendOtp = () =>
    run(async () => {
      const result = await authApi.sendOtp(phoneNumber);
      setDevOtp(result._devOtp ?? null);
      setMessage(result._devOtp ? `OTP sent. Dev code: ${result._devOtp}` : "OTP sent to your phone.");
    });

  const verifyOtp = () =>
    run(async () => {
      const result = await authApi.verifyOtp(phoneNumber, otpCode);
      setSession(result);
      finish(result);
    });

  const loginGoogle = (idToken: string) =>
    run(async () => {
      const result = await authApi.socialSignIn("google", idToken);
      setSession(result);
      finish(result);
    });

  const requestPasswordReset = () =>
    run(async () => {
      const result = await authApi.requestPasswordReset(email);
      setMessage(result.message);
    });

  const resetPassword = () =>
    run(async () => {
      if (!resetToken) throw new Error("This password reset link is incomplete.");
      if (password.length < 8) throw new Error("Password must be at least 8 characters.");
      if (password !== confirmPassword) throw new Error("Passwords do not match.");
      await authApi.resetPassword(resetToken, password);
      setPassword("");
      setConfirmPassword("");
      setMode("email");
      setMessage("Password updated. All previous sessions were signed out; sign in with your new password.");
    });

  const continueDemo = () => {
    startDemoSession();
    finish();
  };

  return (
    <div className="auth-layout">
      <section className="auth-art">
        <div>
          <div className="eyebrow">Skylight account</div>
          <h1>Continue to chat, post, book, and manage your farm profile.</h1>
          <p>
            Web uses the same Skylight identity as mobile. Sign in with email/password or phone OTP.
          </p>
        </div>
      </section>

      <section className="auth-card">
        <div className="eyebrow">Sign in required</div>
        <h2>Access protected workflows</h2>
        {context ? <p style={{ marginTop: 8 }}>You were trying to continue with: <b>{context}</b>.</p> : null}

        <div className="auth-tabs" role="tablist" aria-label="Authentication method">
          <button className={mode === "email" ? "active" : ""} type="button" onClick={() => switchMode("email")}>
            Email
          </button>
          <button className={mode === "phone" ? "active" : ""} type="button" onClick={() => switchMode("phone")}>
            Phone OTP
          </button>
          <button className={mode === "register" ? "active" : ""} type="button" onClick={() => switchMode("register")}>
            Register
          </button>
        </div>

        {mode === "email" ? (
          <form className="auth-form" onSubmit={(event) => { event.preventDefault(); loginEmail(); }}>
            <div className="field">
              <label htmlFor="auth-email">Email</label>
              <input id="auth-email" className="input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" />
            </div>
            <div className="field">
              <label htmlFor="auth-password">Password</label>
              <input id="auth-password" className="input" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Your password" />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Signing in..." : "Sign in"}
            </button>
            <button className="link-button" type="button" onClick={() => switchMode("forgot")}>
              Forgot password?
            </button>
          </form>
        ) : null}

        {mode === "forgot" ? (
          <form className="auth-form" onSubmit={(event) => { event.preventDefault(); requestPasswordReset(); }}>
            <p>Enter your email. The response is intentionally the same whether or not an eligible account exists.</p>
            <div className="field">
              <label htmlFor="forgot-email">Email</label>
              <input id="forgot-email" className="input" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>Send reset instructions</button>
            <button className="link-button" type="button" onClick={() => switchMode("email")}>Back to sign in</button>
          </form>
        ) : null}

        {mode === "reset" ? (
          <form className="auth-form" onSubmit={(event) => { event.preventDefault(); resetPassword(); }}>
            <div className="field">
              <label htmlFor="reset-password">New password</label>
              <input id="reset-password" className="input" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
            </div>
            <div className="field">
              <label htmlFor="reset-password-confirm">Confirm new password</label>
              <input id="reset-password-confirm" className="input" type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>Set new password</button>
          </form>
        ) : null}

        {mode === "phone" ? (
          <form className="auth-form" onSubmit={(event) => { event.preventDefault(); verifyOtp(); }}>
            <div className="field">
              <label htmlFor="auth-phone">Phone number</label>
              <input id="auth-phone" className="input" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} placeholder="+ country code and number" />
            </div>
            <button className="btn btn-ghost" type="button" onClick={sendOtp} disabled={busy || !phoneNumber.trim()}>
              {busy ? "Sending..." : "Send OTP"}
            </button>
            <div className="field">
              <label htmlFor="auth-otp">OTP code</label>
              <input id="auth-otp" className="input" value={otpCode} onChange={(event) => setOtpCode(event.target.value)} placeholder={devOtp ?? "123456"} maxLength={6} />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy || otpCode.length !== 6}>
              Verify and continue
            </button>
          </form>
        ) : null}

        {mode === "register" ? (
          <form className="auth-form" noValidate onSubmit={(event) => { event.preventDefault(); registerEmail(); }}>
            <div className="field">
              <label htmlFor="register-display-name">Display name</label>
              <input id="register-display-name" className="input" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Grace Wanjiru" />
            </div>
            <div className="field">
              <label htmlFor="register-username">Username</label>
              <input id="register-username" className="input" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="grace_farms" />
            </div>
            <div className="field">
              <label htmlFor="register-email">Email</label>
              <input id="register-email" className="input" autoComplete="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" />
            </div>
            <div className="field">
              <label htmlFor="register-password">Password</label>
              <input id="register-password" className="input" autoComplete="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Creating account..." : "Create account"}
            </button>
          </form>
        ) : null}

        {mode === "email" || mode === "phone" || mode === "register" ? (
          <div className="auth-social">
            <div className="auth-social-divider" aria-hidden="true">
              <span>or continue with</span>
            </div>
            <GoogleSignInButton
              busy={busy}
              onCredential={loginGoogle}
              onUnavailable={(googleError) => {
                setMessage(null);
                setError(googleError);
              }}
            />
          </div>
        ) : null}

        {message ? <div className="notice notice-ok">{message}</div> : null}
        {error ? <div className="notice notice-error">{error}</div> : null}

        <div className="auth-demo">
          <button className="link-button" type="button" onClick={continueDemo}>
            Continue with local demo session
          </button>
          <span>Use this only while the local backend is offline.</span>
        </div>

        <p style={{ marginTop: 14, fontSize: 12 }}>
          Protected actions keep their original destination. You will return to <Link href={next}>{next}</Link> after sign in.
        </p>
      </section>
    </div>
  );
}
